<!--
  Created 13.10.2023
  Section template for the proposal show page.
  Supports three different styles: white, gray and aqua.
-->
<template>
  <section
    class="relative print:break-before-page print:!bg-transparent"
    :class="[
      sectionClass,
      { 'hidden': !contentEditable && !sectionVisible }
    ]">
    <!-- Adjustment icon/box -->
    <SectionOptions
      v-if="section && contentEditable"
      class="z-20"
      :section="section" />
    <!-- disabled cursor overlay if section is hidden -->
    <div
      v-if="section"
      class="hidden"
      :class="{ 'absolute left-0 top-0 z-10 !block h-full w-full cursor-not-allowed' : !sectionVisible }" />
    <!-- container div -->
    <div
      class="container mx-auto px-6 py-12 pb-16 print:!py-8 md:py-24 md:pb-28"
      :class="[parentDivClass, { 'pointer-events-none opacity-50' : !sectionVisible }]">
      <div
        class="flex flex-col space-y-12 print:!space-y-8"
        :class="contentsClass">
        <!-- Slot -->
        <slot
          v-if="!$slots.heading"
          name="heading">
          <div class="flex flex-col md:flex-row md:justify-between">
            <slot name="headingTitle">
              <TextEditableTextarea
                v-if="contentEditable && !heading"
                :id="`section-${section}-heading`"
                v-model="store.proposal.data.sections[section].heading"
                background-style="gray"
                class="lg:mr-2 xl:mr-8 2xl:mr-24"
                :text-area-class="'bg-transparent whitespace-pre-wrap md:leading-snug lg:leading-tight '
                  + 'text-2xl md:text-3xl lg:text-4xl font-medium text-gray-800 '
                  + 'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
                :placeholder="'Fill in ' + section + ' heading..'"
                :show-buttons="false"
                :saved-changes="savedChanges"
                @edited="store.unsavedChanges.changes.proposal = true;" />
              <h1
                v-else
                class="text-2xl font-medium md:text-3xl lg:text-4xl"
                :class="headingClass">
                {{ heading || store.proposal.data?.sections[section]?.heading || '' }}
              </h1>
            </slot>
            <!-- Slot -->
            <slot name="headingCta">
              <!-- desktop headingCta -->
              <a
                v-if="cta.text"
                class="hidden items-center space-x-2 font-medium hover:cursor-pointer print:!hidden md:flex md:justify-center"
                :class="[ctaClass, cta.class]"
                @click.prevent="(e) => $emit('headingCtaClicked', e)">
                <p class="inline-block">
                  {{ cta.text }}
                </p>
                <component
                  :is="cta.icon"
                  class="inline-block h-5 w-5" />
              </a>
            </slot>
          </div>
        </slot>
        <!-- Slot -->
        <slot name="content" />
        <!-- Slot -->
        <slot name="footerCta">
          <!-- mobile headingCta -->
          <a
            v-if="cta.text"
            class="flex items-center justify-center space-x-2 font-medium hover:cursor-pointer print:!hidden md:hidden"
            :class="[ctaClass, cta.class]"
            @click.prevent="(e) => $emit('headingCtaClicked', e)">
            <p class="inline-block">
              {{ cta.text }}
            </p>
            <component
              :is="cta.icon"
              class="inline-block h-5 w-5" />
          </a>
          <!-- desktop footerCta -->
          <a
            v-if="ctaFooter.text"
            class="flex items-center justify-center space-x-2 font-medium hover:cursor-pointer print:hidden"
            :class="[ctaClass, ctaFooter.class]"
            @click.prevent="(e) => $emit('footerCtaClicked', e)">
            <p class="inline-block">
              {{ ctaFooter.text }}
            </p>
            <component
              :is="ctaFooter.icon"
              class="inline-block h-5 w-5" />
          </a>
        </slot>
      </div>
    </div>
  </section>
</template>
<script setup>
/* external */
import {
  ArrowRightIcon, ArrowsPointingOutIcon,
} from '@heroicons/vue/24/outline';
import { computed } from 'vue';
import SectionOptions from '@/Pages/Proposal/Components/SectionOptions.vue';
import { useProposalStore } from '@/Stores/Proposal';
import TextEditableTextarea from '@/Components/Forms/TextEditableTextarea.vue';

defineEmits(['headingCtaClicked', 'footerCtaClicked']);

const props = defineProps({
  /**
   * The type of section.
   * This should be the key of the section in the store.proposal.data.sections.
   * @example 'program' or 'activities'
   */
  section: {
    type: String,
    required: false,
    default: '',
  },
  contentEditable: {
    type: Boolean,
    required: false,
    default: false,
  },
  templateStyle: {
    type: String,
    default: 'white', // or 'dark' if you want that to be the default
    validator: (value) => ['white', 'gray', 'aqua'].includes(value),
  },
  contentsClass: {
    type: String,
    required: false,
    default: '',
  },
  parentDivClass: {
    type: String,
    required: false,
    default: '',
  },
  heading: {
    type: String,
    required: false,
    default: '',
  },
  cta: {
    type: Object,
    required: false,
    default: () => ({
      text: '',
      icon: ArrowRightIcon,
      class: '',
    }),
  },
  ctaFooter: {
    type: Object,
    required: false,
    default: () => ({
      text: '',
      icon: ArrowsPointingOutIcon,
      class: '',
    }),
  },
});

const store = useProposalStore();
const savedChanges = computed(() => !store.unsavedChanges.changes.proposal);

const sectionVisible = computed(() => store.proposal.data.sections[props.section]?.visible);

const templateStylesMap = {
  gray: {
    section: 'bg-gray-100',
    heading: 'text-gray-800',
    cta: 'text-gray-800 hover:text-black hover:underline',
  },
  aqua: {
    section: 'bg-aqua-50',
    heading: 'text-gray-800',
    cta: 'text-gray-800 hover:text-black hover:underline',
  },
};

// Default styles if not defined in templateStylesMap
const defaultStyles = {
  section: 'bg-white',
  heading: 'text-gray-800',
  cta: 'text-gray-800 hover:text-black hover:underline',
};

// Individual computed properties for each element:
const sectionClass = computed(() => templateStylesMap[props.templateStyle]?.section || defaultStyles.section);
const headingClass = computed(() => templateStylesMap[props.templateStyle]?.heading || defaultStyles.heading);
const ctaClass = computed(() => templateStylesMap[props.templateStyle]?.cta || defaultStyles.cta);

</script>
